"use client";

import Image from "next/image";
import { FormEvent, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import {
  SiteHeader,
  type SiteHeaderItem,
} from "@/components/layout/site-header";
import styles from "./login.module.css";
import { getApiUrl } from "@/lib/api";

type LoginStatus = {
  message: string;
  tone: "error" | "info" | "success";
};

type LoginResponse = {
  data?: {
    token?: string;
    user?: unknown;
  };
  message?: string;
};

interface LeaderboardStudent {
  rank: number;
  testId: number;
  studentGroupId: number;
  groupUsername: string;
  groupName: string;
  score: string;
  correctCount: number;
  totalQuestions: number;
  duration: number | null;
}

interface LeaderboardSchool {
  schoolId: number;
  schoolName: string;
  students: LeaderboardStudent[];
}

const RANKING_SCHOOLS = [
  {
    key: "HHP",
    school: "HHP",
    fullName: "Trường Cao đẳng Bách Nghệ Hải Phòng",
    logo: "/assets/images/login/ranking-logo-hhp.webp",
    logoWidth: 91,
    logoHeight: 116,
  },
  {
    key: "HACTECH",
    school: "HACTECH",
    fullName: "Trường Cao đẳng nghề Bách Khoa Hà Nội",
    logo: "/assets/images/login/ranking-logo-hactech.webp",
    logoWidth: 157,
    logoHeight: 60,
  },
  {
    key: "TDC",
    school: "TDC",
    fullName: "Trường Cao đẳng Thủ Đức",
    logo: "/assets/images/login/ranking-logo-tdc.webp",
    logoWidth: 161,
    logoHeight: 60,
  },
  {
    key: "CEA",
    school: "CEA",
    fullName: "Trường Cao đẳng Cơ điện và Nông nghiệp Nam bộ",
    logo: "/assets/images/login/ranking-logo-cea.webp",
    logoWidth: 116,
    logoHeight: 107,
  },
  {
    key: "HUEIC",
    school: "HUEIC",
    fullName: "Trường Cao đẳng Công nghiệp Huế",
    logo: "/assets/images/login/ranking-logo-hueic.webp",
    logoWidth: 117,
    logoHeight: 118,
  },
  {
    key: "LTT",
    school: "Lý Tự Trọng",
    fullName: "Trường Cao đẳng Lý Tự Trọng",
    logo: "/assets/images/login/ranking-logo-ltt.webp",
    logoWidth: 144,
    logoHeight: 99,
  },
] as const;

const LOGIN_HEADER_ITEMS: readonly SiteHeaderItem[] = [
  { href: "/", label: "Trang chủ" },
  {
    href: "/login#ranking",
    label: "Bảng xếp hạng",
    isAction: true,
  },
];

export function LoginClient() {
  const router = useRouter();
  const [uid, setUid] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<LoginStatus | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardSchool[]>([]);
  const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(true);

  const apiUrl = getApiUrl();

  useEffect(() => {
    fetch(`${apiUrl}/api/v1/public/leaderboard`)
      .then((res) => res.json())
      .then((json) => {
        if (json.status === "success" && Array.isArray(json.data)) {
          setLeaderboard(json.data);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLeaderboardLoading(false);
      });
  }, [apiUrl]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetch(`${apiUrl}/api/v1/group/auth/me`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (res.ok) {
            router.push("/the-le");
          } else {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            window.dispatchEvent(new Event("auth-change"));
          }
        })
        .catch(() => {});
    }
  }, [router, apiUrl]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!uid.trim() || !password.trim()) {
      setStatus({
        message: "Vui lòng điền đầy đủ tài khoản và mật khẩu.",
        tone: "error",
      });
      return;
    }

    setIsLoading(true);
    setStatus(null);

    try {
      const response = await fetch(`${apiUrl}/api/v1/group/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ groupUsername: uid, password }),
      });
      const data = (await response.json()) as LoginResponse;

      if (!response.ok) {
        throw new Error(
          data.message || "Tài khoản hoặc mật khẩu không chính xác.",
        );
      }

      if (!data.data?.token) {
        throw new Error("Phản hồi đăng nhập không hợp lệ.");
      }

      const userObj = (data.data as any).studentGroup || data.data.user;
      localStorage.removeItem("member_token");
      localStorage.removeItem("member_user");
      localStorage.setItem("token", data.data.token);
      localStorage.setItem("user", JSON.stringify(userObj ?? null));
      window.dispatchEvent(new Event("auth-change"));
      setStatus({
        message: "Đăng nhập thành công! Đang chuyển hướng...",
        tone: "success",
      });
      window.setTimeout(() => router.push("/the-le"), 1000);
    } catch (error: unknown) {
      setStatus({
        message:
          error instanceof Error
            ? error.message
            : "Không thể kết nối đến máy chủ. Vui lòng thử lại sau.",
        tone: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      <a className={styles.skipLink} href="#login-form">
        Đi đến biểu mẫu đăng nhập
      </a>

      <SiteHeader />

      <main className={styles.designStage}>
        <section
          className={styles.hero}
          id="login-home"
          aria-labelledby="login-hero-title"
        >
          <h1 className={styles.srOnly} id="login-hero-title">
            Cuộc thi Kỹ Sư Điện Tập Sự – Khơi nguồn tri thức, dẫn mạch thành
            công
          </h1>

          <form
            className={styles.loginForm}
            id="login-form"
            onSubmit={handleSubmit}
          >
            <div className={styles.field}>
              <label htmlFor="student-id">Tài khoản</label>
              <input
                id="student-id"
                name="student_id"
                type="text"
                autoComplete="username"
                value={uid}
                disabled={isLoading}
                onChange={(event) => setUid(event.target.value)}
                required
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="password">Mật khẩu</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                disabled={isLoading}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>

            <div className={styles.formOptions}>
              <label className={styles.checkLabel}>
                <input name="remember" type="checkbox" disabled={isLoading} />
                <span>Lưu mật khẩu</span>
              </label>
              <button
                className={styles.forgotButton}
                type="button"
                onClick={() =>
                  setStatus({
                    message:
                      "Vui lòng liên hệ Ban Tổ chức để được hỗ trợ khôi phục mật khẩu.",
                    tone: "info",
                  })
                }
              >
                Quên mật khẩu
              </button>
            </div>

            <label className={`${styles.checkLabel} ${styles.termsLabel}`}>
              <input
                name="terms"
                type="checkbox"
                defaultChecked
                disabled={isLoading}
                required
              />
              <span>
                Tôi đồng ý với{" "}
                <a
                  href="https://docs.google.com/document/d/1DzcrySd5lo7bIo1YzEIWNLjY2NYw6FdIij_hBObqklE/edit?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Điều khoản sử dụng và Chính sách bảo vệ dữ liệu cá nhân của Panasonic
                </a>
              </span>
            </label>

            <button
              className={styles.loginSubmit}
              type="submit"
              disabled={isLoading}
              aria-busy={isLoading}
              aria-label={isLoading ? "Đang đăng nhập" : "Đăng nhập"}
            >
              <span className={styles.srOnly}>
                {isLoading ? "Đang đăng nhập..." : "Đăng nhập"}
              </span>
            </button>
          </form>
        </section>

        <div className={styles.sectionTitleFrame}>
          <h2 className={styles.srOnly} id="ranking-title">
            Bảng xếp hạng
          </h2>
        </div>
        <section
          className={styles.ranking}
          id="ranking"
          aria-labelledby="ranking-title"
        >
          <div className={styles.rankingGrid}>
            {RANKING_SCHOOLS.map((ranking) => {
              const schoolData = leaderboard.find((item) => {
                const itemLower = item.schoolName.toLowerCase();
                const schoolLower = ranking.fullName.toLowerCase();
                const keyLower = ranking.key.toLowerCase();
                return (
                  itemLower.includes(keyLower) ||
                  itemLower.includes(ranking.school.toLowerCase()) ||
                  schoolLower.includes(itemLower) ||
                  itemLower.includes(schoolLower)
                );
              });
              const students = schoolData?.students || [];
              const topStudent = students[0];
              const schoolScore = topStudent
                ? (topStudent.totalQuestions > 0
                    ? Math.round((topStudent.correctCount / topStudent.totalQuestions) * 100)
                    : topStudent.correctCount)
                : "";

              return (
                <article
                  className={styles.schoolRanking}
                  aria-label={`Xếp hạng ${ranking.school}`}
                  key={ranking.key}
                >
                  <div className={styles.schoolLogo}>
                    <Image
                      src={ranking.logo}
                      alt={`Logo ${ranking.school}`}
                      width={ranking.logoWidth}
                      height={ranking.logoHeight}
                    />
                  </div>
                  <strong className={styles.schoolScore}>
                    {schoolScore !== "" ? schoolScore : "\u00A0"}
                  </strong>
                  <div className={styles.rankPanel}>
                    {students.length > 0 ? (
                      <ol className={styles.rankList}>
                        {students.slice(0, 5).map((st, index) => (
                          <li key={st.testId || index}>
                            <strong title={st.groupName || st.groupUsername}>
                              #{st.rank || index + 1}: {st.groupName || st.groupUsername}
                            </strong>
                            <span>Điểm số {st.score}</span>
                            <span>
                              Thời gian {st.duration ? Math.ceil(st.duration / 60) : 0} phút
                            </span>
                          </li>
                        ))}
                      </ol>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section
          className={styles.rules}
          id="rules"
          aria-labelledby="rules-title"
        >
          <div
            className={`${styles.sectionTitleFrame} ${styles.rulesTitleFrame}`}
          >
            <h2 id="rules-title">Thể lệ cuộc thi</h2>
          </div>

          <div className={styles.rulesPanel}>
            <div
              className={styles.rulesCopy}
              tabIndex={0}
              aria-label="Nội dung thể lệ cuộc thi"
            >
              <article className={styles.ruleBlock}>
                <h3>Phần 1 – Trắc nghiệm</h3>
                <p>
                  Mỗi câu hỏi sẽ có 04 lựa chọn: A – B – C – D. Thí sinh chọn
                  đúng đáp án để đạt điểm.
                </p>
                <p>
                  Nội dung: Kiến thức ngành điện, an toàn thiết bị điện và sản
                  phẩm Panasonic.
                </p>
                <p>Số lượng câu hỏi: 40 câu hỏi.</p>
                <p>Thời gian: 60 phút.</p>
                <p>
                  <strong>Kết quả:</strong>
                </p>
                <p>
                  Hệ thống chấm dựa trên số câu trả lời đúng và thời gian hoàn
                  thành bài thi.
                </p>
                <p>Điểm số sẽ được hiển thị ngay sau khi nộp bài.</p>
              </article>

              <article className={styles.ruleBlock}>
                <h3>Phần 2 – Vẽ sơ đồ mạch điện</h3>
                <p>
                  Các nhóm thực hiện vẽ 01 sơ đồ mạch điện theo yêu cầu của đề
                  bài.
                </p>
                <p>
                  Có thể vẽ bằng tay hoặc ứng dụng, sau đó xuất ra file PNG,
                  JPEG hoặc PDF.
                </p>
                <p>Bài làm được nộp trực tuyến trên hệ thống.</p>
                <p>
                  <strong>Kết quả:</strong>
                </p>
                <p>
                  Bài thi sẽ được chấm bởi Ban Giám khảo của cuộc thi.
                </p>
                <p>
                  Điểm số sẽ được công bố sau khi Ban Giám khảo hoàn thành việc
                  chấm điểm.
                </p>
                <p>
                  04 trường có điểm số cao nhất sẽ tham gia vòng thi trực tiếp,
                  bao gồm Trường Cao đẳng Bách Nghệ Hải Phòng.
                </p>
              </article>

              <article className={styles.ruleBlock}>
                <h3>Quy định chung</h3>
                <p>Mỗi tài khoản chỉ được tham gia 01 lần.</p>
                <p>
                  Hệ thống sẽ tự động nộp hoặc đóng bài khi hết thời gian.
                </p>
                <p>
                  Trong trường hợp xảy ra trục trặc, vui lòng liên hệ Ban Tổ
                  chức để được hỗ trợ.
                </p>
              </article>
            </div>
          </div>
        </section>
      </main>

      {status && (
        <div
          className={`${styles.loginStatus} ${styles[status.tone]}`}
          role="status"
          aria-live="polite"
        >
          {status.message}
        </div>
      )}
    </div>
  );
}
